import { parseAgrEnvironment } from '@next/core-lib/environment';

import { environment } from './environment';
import { Environment } from './environment.model';

type RuntimeConfig = Record<string, string>;
type RuntimeConfigFetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Pick<Response, 'ok' | 'json'>>;

const parseCSV = (value?: string): string[] =>
  value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const parseBoolean = (value?: string): boolean => value === 'true';

export const parseRuntimeConfig = (config: RuntimeConfig): Environment => {
  const baseEnvironment = parseAgrEnvironment(config);

  return {
    ...baseEnvironment,
    MY_OFFICE_URL: config.MY_OFFICE_URL,
    INSTANT_ORDER: parseBoolean(config.INSTANT_ORDER),
    IDENTIFICATION_TYPES_NL: parseCSV(config.IDENTIFICATION_TYPES_NL),
    IDENTIFICATION_WARNING_PERIOD: config.IDENTIFICATION_WARNING_PERIOD,
  } as Environment;
};

export const loadRuntimeConfig = async (fetcher: RuntimeConfigFetcher = window.fetch.bind(window)): Promise<Environment> => {
  try {
    const response = await fetcher('/assets/config.json', { cache: 'no-store' });
    if (!response.ok) {
      console.warn('No runtime configuration found, using default environment settings.');
      return environment;
    }

    const config: unknown = await response.json();
    if (!config || typeof config !== 'object' || Array.isArray(config) || Object.values(config).some((value) => typeof value !== 'string')) {
      throw new TypeError('Runtime configuration must be an object containing string values.');
    }

    return { ...environment, ...parseRuntimeConfig(config as RuntimeConfig) };
  } catch (error) {
    console.warn('Failed to load runtime environment variables', error);
    return environment;
  }
};
